// oxlint-disable-next-line no-restricted-imports
import { ActionIcon, type ActionIconProps } from "@mantine/core";
import React from "react";

import { colors } from "#app/theme/v2/colors";
import { mergeMantineClassNames } from "#shared/helpers/mantineClassNames";

import { BUTTON_SIZES, ButtonSize } from "../Button/Button";
import classes from "./IconButton.module.css";

type IconButtonVariant = "primary" | "secondary" | "tertiary" | "custom" | "transparent";

const { button } = colors.bg;
const { border, icon } = colors;

const ICON_VARIANT_COLORS: Record<
  IconButtonVariant,
  {
    bg: string;
    hover: string;
    color: string;
    border?: string;
    disabledBg: string;
    disabledColor: string;
  }
> = {
  primary: {
    bg: button.primary.default,
    hover: button.primary.hover,
    color: icon.primaryInverse,
    disabledBg: button.primary.disabled,
    disabledColor: icon.primaryInverse,
  },
  secondary: {
    bg: button.secondary.default,
    hover: button.secondary.hover,
    color: icon.primaryDefault,
    disabledBg: button.secondary.disabled,
    disabledColor: icon.primaryDisabled,
  },
  tertiary: {
    bg: button.tertiary.default,
    hover: button.tertiary.hover,
    color: icon.primaryDefault,
    border: `1px solid ${border.tertiary}`,
    disabledBg: button.tertiary.disabled,
    disabledColor: icon.primaryDisabled,
  },
  custom: {
    bg: button.custom.default,
    hover: button.custom.hover,
    color: icon.primaryDefault,
    disabledBg: button.custom.disabled,
    disabledColor: icon.primaryDisabled,
  },
  transparent: {
    bg: "none",
    hover: button.custom.hover,
    color: icon.secondary,
    disabledBg: button.custom.disabled,
    disabledColor: icon.primaryDisabled,
  },
};

type IconButtonProps = {
  children: React.ReactNode;
  variant?: IconButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: "button" | "submit" | "reset";
  "aria-label": string;
  classNames?: Extract<ActionIconProps["classNames"], Record<string, unknown>>;
};

function iconVariantStyle(c: (typeof ICON_VARIANT_COLORS)[IconButtonVariant]): React.CSSProperties {
  return {
    "--ai-bg": c.bg,
    "--ai-hover": c.hover,
    "--ai-color": c.color,
    "--ai-bd": c.border ?? "none",
    "--btn-disabled-bg": c.disabledBg,
    "--btn-disabled-color": c.disabledColor,
  } as React.CSSProperties;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      children,
      variant = "primary",
      size = "md",
      disabled,
      loading,
      onClick,
      type = "button",
      "aria-label": ariaLabel,
      classNames,
      ...rest
    },
    ref,
  ) => {
    const c = ICON_VARIANT_COLORS[variant];
    const mergedClassNames = mergeMantineClassNames({ root: classes.iconButton }, classNames);

    return (
      <ActionIcon
        ref={ref}
        variant="filled"
        disabled={disabled}
        loading={loading}
        onClick={onClick}
        type={type}
        size={BUTTON_SIZES[size].height}
        aria-label={ariaLabel}
        loaderProps={{ color: c.color }}
        radius="50%"
        classNames={mergedClassNames}
        style={iconVariantStyle(c)}
        {...rest}
      >
        {children}
      </ActionIcon>
    );
  },
);
IconButton.displayName = "IconButton";

export { IconButton };
export type { IconButtonVariant };
